/**
 * Gift voucher formulas: an offer split into sub-products, each with its TTC share of the price
 * and its own VAT rate (for example a dinner at 10 % and wine at 20 %). The offer price is the sum.
 */
export const FORMULA_TAX_RATES = [5.5, 10, 20];
export const FORMULA_MIN_COMPONENTS = 2;
export const FORMULA_MAX_COMPONENTS = 20;

/** Euros typed in the form ("12,5", "12.50", 12.5) to integer cents, or null when not a number. */
export function toCents(value) {
    const amount = Number.parseFloat(String(value ?? "").replace(",", "."));
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
}

export function emptyComponent() {
    return { name: "", price: null, tva: null };
}

/** Starts a formula from the current offer: its price and VAT become the first sub-product. */
export function startFormula({ price = null, tva = null } = {}) {
    return [{ name: "", price, tva }, emptyComponent()];
}

export function formulaTotalCents(components = []) {
    return components.reduce((total, component) => total + Math.max(0, toCents(component.price) ?? 0), 0);
}

/** VAT included in the formula price, by rate, in cents. */
export function vatByRate(components = []) {
    const totals = new Map();
    components.forEach((component) => {
        const cents = toCents(component.price);
        const rate = Number(component.tva);
        if (!cents || cents < 0 || !FORMULA_TAX_RATES.includes(rate)) {
            return;
        }
        totals.set(rate, (totals.get(rate) ?? 0) + cents - cents / (1 + rate / 100));
    });

    return [...totals.entries()].sort(([a], [b]) => a - b).map(([rate, amount]) => ({ rate, amount: Math.round(amount) }));
}

/** Payload expected by the gift voucher API: names trimmed, prices in cents, VAT omitted without VAT. */
export function componentsForApi(components = [], { tvaDisabled = false } = {}) {
    return components.map((component) => ({
        name: String(component.name ?? "").trim(),
        price: toCents(component.price),
        tva: tvaDisabled || component.tva === null || component.tva === "" ? null : Number(component.tva),
    }));
}
